import React, { useEffect, useRef } from 'react';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ToggleOff from '@mui/icons-material/ToggleOff';
import { colors } from './colors';
import { isGermanChristmasSeason } from './seasonalDecor';
import { useAppTheme } from '../themes/useAppTheme';

const isChristmasHolidaysTime = (d: Date = new Date()): boolean => {
    return isGermanChristmasSeason(d);
};

interface Snowflake {
    size: number;
    x: number;
    baseX: number;
    distance: number;
    opacity: number;
    radians: number;
    fallSpeed: number;
    y: number;

    windFactor: number; // [-1..1] how strongly this flake reacts to wind
    turbulence: number; // small extra horizontal wobble

    draw: () => void;
}

export interface SnowProps {
    className?: string;
    style?: React.CSSProperties;

    /**
     * Wind strength in pixels per frame at peak (before per-flake scaling).
     * Typical values: 0..2
     */
    windStrength?: number;

    /**
     * How “gusty” the wind is. 0 = steady, 1 = frequent/strong gusts.
     * Typical values: 0..1
     */
    gustiness?: number;

    /**
     * If true, mouse position influences wind direction/strength.
     */
    mouseWind?: boolean;

    /**
     * Max multiplier for snow amount over time (cap).
     * Requested: not more than 1.75
     */
    maxSnowMultiplier?: number;

    /**
     * Time to reach max multiplier (ms).
     */
    rampUpMs?: number;
}

const AMOUNT_OF_FLAKES = 0.7;

const STORAGE_KEY = 'ui:snowEnabled';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const Snow: React.FC<SnowProps> = ({
    className,
    style,
    windStrength = 1.0,
    gustiness = 0.15,
    mouseWind = true,
    maxSnowMultiplier = 1.75,
    rampUpMs = 120_000
}) => {
    const isItTime = isChristmasHolidaysTime();
    const theme = useAppTheme();
    const flakeOpacityMultiplier = theme.palette.isDarkTheme ? 0.45 : 1;
    const snowToggleColor = theme.palette.isDarkTheme ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.58)';

    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rafRef = useRef<number | null>(null);

    const [enabled, setEnabled] = React.useState<boolean>(() => {
        if (!isItTime) return false;

        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            return raw == null ? true : raw === 'true';
        } catch {
            return true;
        }
    });

    const toggle = React.useCallback(() => {
        setEnabled(prev => {
            const next = !prev;
            try {
                window.localStorage.setItem(STORAGE_KEY, String(next));
            } catch {
                // ignore
            }
            return next;
        });
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // overlay container styles
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.right = '0';
        container.style.bottom = '0';
        container.style.zIndex = '1000';
        container.style.pointerEvents = 'none';

        // create canvas once and append to container
        if (!canvasRef.current) {
            const canvas = document.createElement('canvas');
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.display = 'block';
            canvasRef.current = canvas;
            container.appendChild(canvas);
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        // If disabled: stop animations and clear
        if (!enabled) {
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            resizeCanvas();
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        let flakes: Snowflake[] = [];

        // Wind model (Xsnow-like): slowly varying base wind + gust target + gentle oscillation
        let wind = 0; // current wind px/frame
        let windTarget = 0; // target wind px/frame
        let gustPhase = Math.random() * Math.PI * 2;

        // Mouse influence state (normalized)
        let mouseXNorm = 0; // -1..1 (left..right)
        let mousePush = 0; // -1..1 based on horizontal mouse velocity
        let lastMouseX: number | null = null;
        let lastMouseT: number | null = null;

        // Snow amount ramp state
        const startedAt = nowMs();
        let baseFlakeCount = 0;

        let retargetTimer: number | null = null;

        const computeBaseCount = () => Math.ceil(((canvas.width * canvas.height) / 15000) * AMOUNT_OF_FLAKES);

        const onPointerMove = (e: PointerEvent) => {
            const w = window.innerWidth || 1;
            mouseXNorm = clamp((e.clientX / w) * 2 - 1, -1, 1);

            const t = nowMs();
            if (lastMouseX != null && lastMouseT != null) {
                const dx = e.clientX - lastMouseX;
                const dt = Math.max(1, t - lastMouseT);
                const vx = dx / dt; // px/ms

                // scale to [-1..1] (tune multiplier for sensitivity)
                mousePush = clamp(vx * 18, -1, 1);
            }
            lastMouseX = e.clientX;
            lastMouseT = t;
        };

        const retargetWind = () => {
            // Random walk target; can go negative (wind reversal).
            const range = windStrength * (0.6 + gustiness * 1.6);
            const randomTarget = (Math.random() * 2 - 1) * range;

            if (!mouseWind) {
                windTarget = randomTarget;
                return;
            }

            // Mouse influence:
            // - position biases wind direction (cursor on right => wind to the right)
            // - quick horizontal mouse movement adds a short "push"
            const posBias = mouseXNorm * windStrength * 1.3;
            const velBias = mousePush * windStrength * 1.8;

            windTarget = randomTarget * 0.35 + posBias + velBias;
        };

        const scheduleRetarget = () => {
            if (retargetTimer != null) window.clearInterval(retargetTimer);
            const minMs = 900;
            const maxMs = 3500;
            const ms = Math.round(maxMs - gustiness * (maxMs - minMs));
            retargetTimer = window.setInterval(retargetWind, ms);
        };

        const createFlake = (): Snowflake => {
            const size = Math.random() * 2 + 2;

            const sizeNorm = clamp((size - 2) / 2, 0, 1); // 2..4 => 0..1
            const windFactor = clamp((1 - sizeNorm) * 1.2 + (Math.random() * 0.6 - 0.3), -1, 1);

            const flake: Snowflake = {
                size,
                x: Math.random() * canvas.width,
                baseX: 0,
                distance: Math.random() * 50 + 1,
                opacity: Math.random(),
                radians: Math.random() * Math.PI * 2,
                fallSpeed: Math.random() * 1.5 + 0.5,
                y: Math.random() * canvas.height,

                windFactor,
                turbulence: Math.random() * 0.6 + 0.1,

                draw: () => {
                    // vertical motion
                    if (flake.y > canvas.height + flake.size) {
                        flake.y = -flake.size;
                        flake.baseX = Math.random() * canvas.width;
                        flake.x = flake.baseX;
                    } else {
                        flake.y += flake.fallSpeed;
                    }

                    // gentle wobble
                    flake.radians += 0.02;
                    const wobble = flake.distance * Math.sin(flake.radians);

                    // wind pushes baseX so flakes drift across the whole screen
                    const perFlakeWind = wind * flake.windFactor;
                    const micro = flake.turbulence * Math.sin(flake.radians * 2.2 + flake.baseX * 0.01);

                    flake.baseX += perFlakeWind + micro;

                    // wrap horizontally
                    if (flake.baseX < -50) flake.baseX = canvas.width + 50;
                    if (flake.baseX > canvas.width + 50) flake.baseX = -50;

                    flake.x = flake.baseX + wobble;

                    // draw glyph
                    ctx.save();
                    ctx.globalAlpha = flake.opacity * flakeOpacityMultiplier;
                    ctx.fillStyle = 'rgb(173,216,230)';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.font = `${Math.max(10, flake.size * 6)}px sans-serif`;
                    ctx.fillText('❄', flake.x, flake.y);
                    ctx.restore();
                }
            };

            flake.baseX = flake.x;
            return flake;
        };

        const setup = () => {
            resizeCanvas();

            flakes = [];
            baseFlakeCount = computeBaseCount();
            for (let i = 0; i < baseFlakeCount; i++) flakes.push(createFlake());
        };

        const onResize = () => {
            // keep "time on page" progression; just recompute base density for new viewport
            resizeCanvas();
            baseFlakeCount = computeBaseCount();
        };

        const animate = () => {
            rafRef.current = window.requestAnimationFrame(animate);

            // continuously steer wind target toward mouse while enabled
            if (mouseWind) {
                const posBias = mouseXNorm * windStrength * 1.3;
                const velBias = mousePush * windStrength * 1.8;

                windTarget += (posBias + velBias - windTarget) * 0.02;
                mousePush *= 0.92; // decay the gust push
            }

            // update wind: ease towards target + slow oscillation
            gustPhase += 0.01 + gustiness * 0.02;
            const gust = Math.sin(gustPhase) * windStrength * 0.35 * gustiness;

            const ease = 0.006 + gustiness * 0.01;
            wind += (windTarget - wind) * ease;

            const maxWind = windStrength * (2.2 + gustiness * 1.2);
            wind = clamp(wind + gust, -maxWind, maxWind);

            // ramp snow amount over time (only grows, capped)
            const elapsed = Math.max(0, nowMs() - startedAt);
            const t = clamp(elapsed / Math.max(1, rampUpMs), 0, 1);
            const multiplier = 1 + (clamp(maxSnowMultiplier, 1, 10) - 1) * t;

            const desiredCount = Math.ceil(baseFlakeCount * multiplier);
            while (flakes.length < desiredCount) flakes.push(createFlake());

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (const flake of flakes) flake.draw();
        };

        window.addEventListener('resize', onResize);
        if (mouseWind) window.addEventListener('pointermove', onPointerMove, { passive: true });

        retargetWind();
        scheduleRetarget();
        setup();
        animate();

        return () => {
            window.removeEventListener('resize', onResize);
            if (mouseWind) window.removeEventListener('pointermove', onPointerMove);

            if (retargetTimer != null) {
                window.clearInterval(retargetTimer);
                retargetTimer = null;
            }

            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }

            if (canvasRef.current && canvasRef.current.parentElement === container) {
                container.removeChild(canvasRef.current);
            }
            canvasRef.current = null;
        };
    }, [enabled, windStrength, gustiness, mouseWind, maxSnowMultiplier, rampUpMs, flakeOpacityMultiplier]);

    if (!isItTime) return null;

    return (
        <React.Fragment>
            <button
                type="button"
                aria-label={enabled ? 'Schnee deaktivieren' : 'Schnee aktivieren'}
                title={enabled ? 'Schnee deaktivieren' : 'Schnee aktivieren'}
                onClick={toggle}
                style={{
                    position: 'fixed',
                    top: 39,
                    right: 2,
                    padding: '2px 1px 2px 1px',
                    border: 'none',
                    borderRadius: '4px 0 0 4px',
                    cursor: 'pointer',
                    zIndex: 2001, // above snow canvas (1000)
                    opacity: enabled ? 0.95 : 0.45,
                    pointerEvents: 'auto',
                    background: 'transparent',
                    fontSize: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                }}
            >
                <AcUnitIcon sx={{ fill: snowToggleColor, width: 14, height: 14 }} />
                <ToggleOff sx={{ fill: snowToggleColor, width: 14, height: 14 }} />
            </button>

            <div ref={containerRef} className={className} style={style} />
        </React.Fragment>
    );
};

export default Snow;
